// Service/Configuration Intelligence Automation Data
// Supports: Intent in, PR out, pipeline applies model

// ============================================================================
// DRIFT AND POLICY FINDINGS
// ============================================================================
export const driftFindings = [
  {
    id: 'drift-auth-replicas',
    type: 'drift',
    severity: 'medium',
    service: 'auth-service',
    finding: 'Replica count drifted from baseline',
    details: {
      expected: 3,
      actual: 5,
      field: 'spec.replicas',
      driftedAt: '2026-01-10T14:30:00Z',
      changedBy: 'kubectl scale (manual)'
    },
    impact: 'Increased cost, potential inconsistency with IaC',
    actions: {
      revert: {
        label: 'Create revert PR',
        description: 'Scale back to 3 replicas per Terraform state',
        changeType: 'drift_revert'
      },
      accept: {
        label: 'Accept as baseline PR',
        description: 'Update Terraform to reflect 5 replicas',
        changeType: 'baseline_update'
      },
      exception: {
        label: 'Create exception',
        description: 'Document temporary deviation with expiry'
      }
    }
  },
  {
    id: 'drift-vpc-sg',
    type: 'policy_violation',
    severity: 'high',
    service: 'prod-vpc',
    finding: 'Security group drift: unauthorized port open',
    details: {
      expected: 'Ports 443, 80 only',
      actual: 'Ports 443, 80, 8080',
      field: 'security_group.ingress_rules',
      driftedAt: '2026-01-08T09:15:00Z',
      changedBy: 'AWS Console (sarah.dev@company.com)'
    },
    impact: 'Security exposure - port 8080 allows direct API access',
    actions: {
      revert: {
        label: 'Create revert PR',
        description: 'Close port 8080, restore security posture',
        changeType: 'security_revert'
      },
      accept: {
        label: 'Accept as baseline PR',
        description: 'Add port 8080 to Terraform with documentation',
        changeType: 'baseline_update'
      },
      exception: {
        label: 'Create exception',
        description: 'Temporary exception for testing'
      }
    }
  },
  {
    id: 'drift-checkout-env',
    type: 'drift',
    severity: 'low',
    service: 'checkout-service',
    finding: 'Environment variable drift',
    details: {
      expected: 'LOG_LEVEL=info',
      actual: 'LOG_LEVEL=debug',
      field: 'spec.containers[0].env',
      driftedAt: '2026-01-11T08:00:00Z',
      changedBy: 'kubectl set env (debug session)'
    },
    impact: 'Increased log volume, minor cost impact',
    actions: {
      revert: {
        label: 'Create revert PR',
        description: 'Reset LOG_LEVEL to info',
        changeType: 'drift_revert'
      },
      accept: {
        label: 'Accept as baseline PR',
        description: 'Keep debug logging permanently',
        changeType: 'baseline_update'
      },
      exception: {
        label: 'Create exception',
        description: 'Allow debug mode for incident investigation'
      }
    }
  },
  {
    id: 'drift-payments-tls',
    type: 'policy_violation',
    severity: 'high',
    service: 'payments-db',
    finding: 'TLS enforcement disabled',
    details: {
      expected: 'require_ssl = true',
      actual: 'require_ssl = false',
      field: 'rds.parameter_group.ssl',
      driftedAt: '2026-01-09T16:45:00Z',
      changedBy: 'RDS Console (ops@company.com)'
    },
    impact: 'Data in transit not encrypted - compliance violation',
    actions: {
      revert: {
        label: 'Create revert PR',
        description: 'Re-enable TLS requirement',
        changeType: 'security_revert'
      },
      accept: {
        label: 'Accept as baseline PR',
        description: 'Not recommended - compliance issue',
        changeType: 'baseline_update',
        blocked: true
      },
      exception: {
        label: 'Create exception',
        description: 'Requires security team approval'
      }
    }
  }
];

// ============================================================================
// SERVICE CHANGE SPECS
// ============================================================================
export const serviceChangeSpecs = {
  'drift-revert-auth-replicas': {
    id: 'CS-2026-0100',
    title: 'Revert auth-service replica drift',
    type: 'drift_revert',
    version: '1.0',
    targets: [
      { id: 'auth-service', name: 'auth-service', environment: 'production' }
    ],
    drift: {
      field: 'spec.replicas',
      expected: 3,
      actual: 5,
      action: 'revert_to_expected'
    },
    constraints: {
      maintenance_window_required: false,
      rollback_required: true,
      dry_run: true
    },
    risk: 'medium',
    required_approvals: ['Platform Lead'],
    generated_artifacts: {
      plan: 'revert-replicas-plan.yaml',
      policy_simulation: 'replica-policy-sim.json',
      tests: 'replica-tests.yaml'
    },
    policy_checks: [
      { name: 'Replica Baseline', status: 'violation', note: 'Current 5, expected 3' },
      { name: 'HPA Compatibility', status: 'pass', note: 'HPA min/max within bounds' },
      { name: 'Cost Policy', status: 'warning', note: 'Over-provisioned by 2 pods' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'drift-revert-vpc-sg': {
    id: 'CS-2026-0101',
    title: 'Revert prod-vpc security group - close port 8080',
    type: 'security_revert',
    version: '1.0',
    targets: [
      { id: 'prod-vpc', name: 'prod-vpc', resource_type: 'aws_security_group' }
    ],
    drift: {
      field: 'ingress_rules',
      expected: ['443', '80'],
      actual: ['443', '80', '8080'],
      action: 'remove_unauthorized_port'
    },
    constraints: {
      maintenance_window_required: true,
      rollback_required: true,
      dry_run: true,
      security_review: true
    },
    risk: 'high',
    required_approvals: ['Security Team', 'Platform Lead', 'Change Advisory Board'],
    generated_artifacts: {
      plan: 'sg-revert-plan.yaml',
      policy_simulation: 'security-policy-sim.json',
      tests: 'sg-connectivity-tests.yaml'
    },
    policy_checks: [
      { name: 'Port Allowlist', status: 'violation', note: 'Port 8080 not in allowlist' },
      { name: 'Change Tracking', status: 'pass', note: 'Manual change detected and logged' },
      { name: 'Compliance', status: 'required', note: 'SOC2 requires port restriction' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'service-spec-checkout': {
    id: 'CS-2026-0102',
    title: 'Service Spec - checkout-service',
    type: 'service_spec',
    version: '1.0',
    targets: [
      { id: 'checkout-service', name: 'checkout-service', type: 'Backend API' }
    ],
    service_spec: {
      name: 'checkout-service',
      type: 'backend',
      technology: 'Node.js',
      replicas: { min: 2, max: 10, desired: 3 },
      resources: { cpu: '500m', memory: '512Mi' },
      data_class: 'pci',
      auth_requirements: ['jwt', 'api_key'],
      allowed_egress: ['payments-db', 'redis-cache', 'stripe-api']
    },
    constraints: {
      maintenance_window_required: false,
      rollback_required: true,
      dry_run: true
    },
    risk: 'medium',
    required_approvals: ['Payments Team Lead', 'Platform Lead'],
    generated_artifacts: {
      terraform: 'checkout-infra.tf',
      helm_values: 'checkout-values.yaml',
      iam_policy: 'checkout-iam.json',
      network_policy: 'checkout-netpol.yaml'
    },
    policy_checks: [
      { name: 'PCI Compliance', status: 'pass', note: 'Data class PCI - encryption enabled' },
      { name: 'Egress Policy', status: 'pass', note: 'Only approved destinations' },
      { name: 'Auth Requirements', status: 'pass', note: 'JWT + API key enforced' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'runbook-checkout': {
    id: 'CS-2026-0103',
    title: 'Generate Runbook - checkout-service',
    type: 'runbook_generation',
    version: '1.0',
    targets: [
      { id: 'checkout-service', name: 'checkout-service' }
    ],
    runbook: {
      name: 'checkout-service-runbook',
      includes: ['deployment', 'rollback', 'scaling', 'incident_response'],
      retries: true,
      compensations: true,
      escalation: true
    },
    constraints: {
      maintenance_window_required: false,
      rollback_required: false,
      dry_run: false
    },
    risk: 'low',
    required_approvals: ['Payments Team Lead'],
    generated_artifacts: {
      workflow: 'checkout-runbook.yaml',
      tests: 'runbook-simulation.yaml'
    },
    policy_checks: [
      { name: 'Runbook Template', status: 'pass', note: 'Using approved template v2.1' },
      { name: 'Escalation Path', status: 'pass', note: 'PagerDuty routing configured' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'enforce-baseline-auth': {
    id: 'CS-2026-0104',
    title: 'Enforce Baseline - auth-service',
    type: 'baseline_enforcement',
    version: '1.0',
    targets: [
      { id: 'auth-service', name: 'auth-service' }
    ],
    baseline: {
      replicas: 3,
      resources: { cpu: '250m', memory: '256Mi' },
      env: { LOG_LEVEL: 'info', NODE_ENV: 'production' }
    },
    constraints: {
      maintenance_window_required: false,
      rollback_required: true,
      dry_run: true
    },
    risk: 'medium',
    required_approvals: ['Platform Lead'],
    generated_artifacts: {
      terraform: 'auth-baseline.tf',
      reconciler: 'auth-reconcile.yaml'
    },
    policy_checks: [
      { name: 'Baseline Match', status: 'violation', note: 'Replicas drifted from 3 to 5' },
      { name: 'Resource Limits', status: 'pass', note: 'Within cluster quotas' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'hardening-payments-db': {
    id: 'CS-2026-0105',
    title: 'Failover Hardening - payments-db',
    type: 'hardening',
    version: '1.0',
    targets: [
      { id: 'payments-db', name: 'payments-db', type: 'database' }
    ],
    hardening: {
      multi_az: true,
      backup_retention: 30,
      point_in_time_recovery: true,
      canary_health_check: true,
      failover_priority: 1
    },
    constraints: {
      maintenance_window_required: true,
      rollback_required: true,
      dry_run: true
    },
    risk: 'high',
    required_approvals: ['Data Team Lead', 'Platform Lead', 'Change Advisory Board'],
    generated_artifacts: {
      terraform: 'payments-db-hardening.tf',
      tests: 'failover-tests.yaml',
      canary: 'payments-canary.yaml'
    },
    policy_checks: [
      { name: 'HA Policy', status: 'required', note: 'P1 database requires multi-AZ' },
      { name: 'Backup Policy', status: 'pass', note: '30 day retention meets compliance' },
      { name: 'DR Plan', status: 'pass', note: 'Failover tested within 30 days' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'contract-tests-payments': {
    id: 'CS-2026-0106',
    title: 'Contract Tests - payments-db dependencies',
    type: 'contract_tests',
    version: '1.0',
    targets: [
      { id: 'payments-db', name: 'payments-db' },
      { id: 'checkout-service', name: 'checkout-service' },
      { id: 'order-service', name: 'order-service' },
      { id: 'refund-service', name: 'refund-service' }
    ],
    contracts: {
      provider: 'payments-db',
      consumers: ['checkout-service', 'order-service', 'refund-service'],
      test_type: 'pact',
      include_fixtures: true
    },
    constraints: {
      maintenance_window_required: false,
      rollback_required: false,
      dry_run: false
    },
    risk: 'low',
    required_approvals: ['Data Team Lead'],
    generated_artifacts: {
      pact_tests: 'payments-contracts.pact.js',
      fixtures: 'payments-fixtures.json',
      ci_config: 'contract-ci.yaml'
    },
    policy_checks: [
      { name: 'Contract Coverage', status: 'pass', note: '3 consumers covered' },
      { name: 'Schema Validation', status: 'pass', note: 'All schemas validated' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'network-policy-edge': {
    id: 'CS-2026-0107',
    title: 'Network Policy - checkout to payments-db',
    type: 'network_policy',
    version: '1.0',
    targets: [
      { from: 'checkout-service', to: 'payments-db', protocol: 'tcp', port: 5432 }
    ],
    policy: {
      restrict_egress: true,
      enforce_tls: true,
      allowed_namespaces: ['payments'],
      deny_by_default: true
    },
    constraints: {
      maintenance_window_required: false,
      rollback_required: true,
      dry_run: true
    },
    risk: 'medium',
    required_approvals: ['Security Team', 'Platform Lead'],
    generated_artifacts: {
      network_policy: 'checkout-payments-netpol.yaml',
      tests: 'connectivity-tests.yaml'
    },
    policy_checks: [
      { name: 'TLS Enforcement', status: 'pass', note: 'mTLS enabled' },
      { name: 'Egress Restriction', status: 'pass', note: 'Only payments-db:5432 allowed' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'slo-alerts': {
    id: 'CS-2026-0108',
    title: 'SLO and Alert Rules - checkout to payments-db',
    type: 'slo_alerts',
    version: '1.0',
    targets: [
      { service: 'checkout-service', dependency: 'payments-db' }
    ],
    slos: {
      latency_p99: '100ms',
      error_rate: '0.1%',
      availability: '99.9%'
    },
    alerts: {
      latency_warning: '80ms',
      latency_critical: '150ms',
      error_rate_warning: '0.05%',
      error_rate_critical: '0.2%'
    },
    constraints: {
      maintenance_window_required: false,
      rollback_required: false,
      dry_run: false
    },
    risk: 'low',
    required_approvals: ['Payments Team Lead'],
    generated_artifacts: {
      prometheus_rules: 'checkout-slo-rules.yaml',
      grafana_dashboard: 'checkout-dashboard.json',
      pagerduty_config: 'checkout-alerts.yaml'
    },
    policy_checks: [
      { name: 'SLO Policy', status: 'pass', note: 'Meets P1 service requirements' },
      { name: 'Alert Coverage', status: 'pass', note: 'All critical paths monitored' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'fix-ownership': {
    id: 'CS-2026-0109',
    title: 'Fix Missing Ownership - unowned services',
    type: 'ownership_fix',
    version: '1.0',
    targets: [
      { id: 'legacy-api', name: 'legacy-api', current_owner: null },
      { id: 'batch-processor', name: 'batch-processor', current_owner: null }
    ],
    ownership: {
      legacy_api: { team: 'Platform', lead: 'Mike Chen', on_call: 'platform-oncall' },
      batch_processor: { team: 'Data', lead: 'Sarah Kim', on_call: 'data-oncall' }
    },
    constraints: {
      maintenance_window_required: false,
      rollback_required: false,
      dry_run: false
    },
    risk: 'low',
    required_approvals: ['Platform Lead', 'Data Team Lead'],
    generated_artifacts: {
      codeowners: 'CODEOWNERS',
      service_metadata: 'service-owners.yaml',
      pagerduty_mapping: 'oncall-routes.yaml'
    },
    policy_checks: [
      { name: 'Ownership Policy', status: 'violation', note: '2 services without owners' },
      { name: 'On-Call Coverage', status: 'required', note: 'All services need on-call' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  },
  'escalation-route': {
    id: 'CS-2026-0110',
    title: 'Create Escalation Route - payments team',
    type: 'escalation_route',
    version: '1.0',
    targets: [
      { team: 'Payments', services: ['checkout-service', 'order-service', 'refund-service'] }
    ],
    escalation: {
      level_1: { type: 'oncall', schedule: 'payments-primary', timeout: '5m' },
      level_2: { type: 'oncall', schedule: 'payments-secondary', timeout: '10m' },
      level_3: { type: 'manager', user: 'payments-lead', timeout: '15m' },
      level_4: { type: 'exec', user: 'cto', timeout: '30m' }
    },
    constraints: {
      maintenance_window_required: false,
      rollback_required: false,
      dry_run: false
    },
    risk: 'low',
    required_approvals: ['Payments Team Lead'],
    generated_artifacts: {
      pagerduty_policy: 'payments-escalation.yaml',
      oncall_schedules: 'payments-schedules.yaml'
    },
    policy_checks: [
      { name: 'Escalation Policy', status: 'pass', note: '4-tier escalation configured' },
      { name: 'Response SLA', status: 'pass', note: 'Meets P1 response requirements' }
    ],
    audit: { event_schema_version: '1.0', created_at: '2026-01-11T14:00:00Z', created_by: 'Config Intelligence' }
  }
};

// ============================================================================
// DEPENDENCY NODE DETAILS
// ============================================================================
export const dependencyNodeDetails = {
  'payments-db': {
    type: 'database',
    criticality: 'P1',
    team: 'Data Team',
    blastRadius: {
      level: 'high',
      directDependents: 3,
      transitiveDependents: 9,
      affectedUsers: '~50,000'
    },
    singlePointsOfFailure: [
      'Primary database instance (no read replicas in failover config)',
      'Connection pooler (PgBouncer single instance)'
    ],
    recentChanges: [
      { date: '2026-01-10', action: 'Schema migration - added index', by: 'data-team' },
      { date: '2026-01-08', action: 'Backup retention changed to 30 days', by: 'ops' },
      { date: '2026-01-05', action: 'Connection limit increased to 500', by: 'platform' }
    ],
    suggestedActions: [
      { id: 'hardening-payments-db', label: 'Add failover hardening', description: 'Multi-AZ, backup policy, canary health check' },
      { id: 'contract-tests-payments', label: 'Generate dependency contract tests', description: 'Pact tests for 3 consumers' }
    ]
  },
  'checkout-service': {
    type: 'service',
    criticality: 'P1',
    team: 'Payments Team',
    blastRadius: {
      level: 'high',
      directDependents: 2,
      transitiveDependents: 3,
      affectedUsers: '~30,000'
    },
    singlePointsOfFailure: [
      'Stripe API dependency (no fallback payment processor)',
      'Redis cache (cache-aside pattern, no graceful degradation)'
    ],
    recentChanges: [
      { date: '2026-01-11', action: 'LOG_LEVEL changed to debug', by: 'debug-session' },
      { date: '2026-01-09', action: 'Deployed v2.4.1 - bug fix', by: 'payments-ci' },
      { date: '2026-01-07', action: 'HPA max replicas increased', by: 'platform' }
    ],
    suggestedActions: [
      { id: 'service-spec-checkout', label: 'Create service spec PR', description: 'Terraform, Helm, IAM, DNS, TLS, LB wiring' },
      { id: 'runbook-checkout', label: 'Generate runbook PR', description: 'Deployment, rollback, scaling, incident response' }
    ]
  },
  'auth-service': {
    type: 'service',
    criticality: 'P1',
    team: 'Platform Team',
    blastRadius: {
      level: 'critical',
      directDependents: 5,
      transitiveDependents: 12,
      affectedUsers: '~100,000'
    },
    singlePointsOfFailure: [
      'Identity provider dependency (Okta)',
      'Token signing key (single key, no rotation automation)'
    ],
    recentChanges: [
      { date: '2026-01-10', action: 'Replica count manually scaled to 5', by: 'kubectl' },
      { date: '2026-01-06', action: 'JWT expiry changed to 1h', by: 'security-review' }
    ],
    suggestedActions: [
      { id: 'enforce-baseline-auth', label: 'Enforce baseline PR', description: 'Reconcile to Terraform desired state' },
      { id: 'escalation-route', label: 'Create escalation route', description: 'PagerDuty routing and on-call mapping' }
    ]
  }
};

// ============================================================================
// EDGE (DEPENDENCY) ACTIONS
// ============================================================================
export const dependencyEdgeActions = {
  'payments-db->checkout-service': {
    from: 'payments-db',
    to: 'checkout-service',
    actions: [
      { id: 'network-policy-edge', label: 'Generate network policy PR', description: 'Restrict egress, enforce TLS' },
      { id: 'contract-tests-payments', label: 'Generate contract test PR', description: 'Pact tests between provider and consumer' },
      { id: 'slo-alerts', label: 'Generate SLO and alert rules PR', description: 'Latency, error rate, availability SLOs' }
    ]
  },
  'checkout-service->frontend-web': {
    from: 'checkout-service',
    to: 'frontend-web',
    actions: [
      { id: 'network-policy-edge', label: 'Generate network policy PR', description: 'Restrict ingress to frontend only' },
      { id: 'slo-alerts', label: 'Generate SLO and alert rules PR', description: 'API response time SLOs' }
    ]
  }
};

// ============================================================================
// FAILURE SIMULATION RESULTS
// ============================================================================
export const failureSimulationResults = {
  'payments-db': {
    simulation: 'Database failure - primary instance unavailable',
    duration: '5 minutes',
    results: {
      impactedServices: ['checkout-service', 'order-service', 'refund-service'],
      impactedUsers: '~50,000',
      revenueImpact: '$125,000 (estimated)',
      mttr: '15 minutes (with current failover)',
      mttrWithHardening: '< 1 minute (with multi-AZ)'
    },
    findings: [
      { severity: 'critical', finding: 'No automatic failover configured' },
      { severity: 'high', finding: 'Connection pooler is single point of failure' },
      { severity: 'medium', finding: 'Dependent services lack circuit breakers' }
    ],
    hardeningRecommendations: [
      'Enable Multi-AZ deployment',
      'Add read replica for failover',
      'Configure PgBouncer redundancy',
      'Add circuit breakers to dependent services'
    ],
    changeSpecId: 'hardening-payments-db'
  }
};

// ============================================================================
// COMPILED DIFFS FOR SERVICES
// ============================================================================
export const serviceCompiledDiffs = {
  terraform: `# Terraform - checkout-service infrastructure
# Generated from Service Spec CS-2026-0102

resource "aws_ecs_service" "checkout" {
  name            = "checkout-service"
  cluster         = aws_ecs_cluster.main.id
  task_definition = aws_ecs_task_definition.checkout.arn
  desired_count   = 3
  
  deployment_configuration {
    minimum_healthy_percent = 50
    maximum_percent         = 200
  }
  
  network_configuration {
    subnets          = var.private_subnet_ids
    security_groups  = [aws_security_group.checkout.id]
    assign_public_ip = false
  }
  
  load_balancer {
    target_group_arn = aws_lb_target_group.checkout.arn
    container_name   = "checkout"
    container_port   = 3000
  }
}

resource "aws_security_group" "checkout" {
  name        = "checkout-service-sg"
  description = "Security group for checkout-service"
  vpc_id      = var.vpc_id
  
  ingress {
    from_port       = 3000
    to_port         = 3000
    protocol        = "tcp"
    security_groups = [aws_security_group.alb.id]
  }
  
  egress {
    from_port   = 5432
    to_port     = 5432
    protocol    = "tcp"
    cidr_blocks = [var.db_cidr]
    description = "PostgreSQL - payments-db"
  }
  
  egress {
    from_port   = 6379
    to_port     = 6379
    protocol    = "tcp"
    cidr_blocks = [var.redis_cidr]
    description = "Redis - redis-cache"
  }
}`,

  helm: `# Helm Values - checkout-service
# Generated from Service Spec CS-2026-0102

replicaCount: 3

image:
  repository: company/checkout-service
  tag: "latest"
  pullPolicy: IfNotPresent

resources:
  requests:
    cpu: "500m"
    memory: "512Mi"
  limits:
    cpu: "1000m"
    memory: "1024Mi"

autoscaling:
  enabled: true
  minReplicas: 2
  maxReplicas: 10
  targetCPUUtilizationPercentage: 70

service:
  type: ClusterIP
  port: 3000

ingress:
  enabled: true
  className: "nginx"
  annotations:
    nginx.ingress.kubernetes.io/ssl-redirect: "true"
  hosts:
    - host: checkout.internal.company.com
      paths:
        - path: /
          pathType: Prefix
  tls:
    - secretName: checkout-tls
      hosts:
        - checkout.internal.company.com

env:
  - name: NODE_ENV
    value: "production"
  - name: LOG_LEVEL
    value: "info"
  - name: DATABASE_URL
    valueFrom:
      secretKeyRef:
        name: checkout-secrets
        key: database-url`,

  networkPolicy: `# Network Policy - checkout-service
# Generated from Service Spec CS-2026-0102

apiVersion: networking.k8s.io/v1
kind: NetworkPolicy
metadata:
  name: checkout-service-netpol
  namespace: payments
spec:
  podSelector:
    matchLabels:
      app: checkout-service
  policyTypes:
    - Ingress
    - Egress
  ingress:
    - from:
        - namespaceSelector:
            matchLabels:
              name: frontend
        - podSelector:
            matchLabels:
              app: frontend-web
      ports:
        - protocol: TCP
          port: 3000
  egress:
    - to:
        - namespaceSelector:
            matchLabels:
              name: data
        - podSelector:
            matchLabels:
              app: payments-db
      ports:
        - protocol: TCP
          port: 5432
    - to:
        - namespaceSelector:
            matchLabels:
              name: cache
        - podSelector:
            matchLabels:
              app: redis-cache
      ports:
        - protocol: TCP
          port: 6379`,

  securityGroupRevert: `# Terraform - Security Group Revert
# Removes unauthorized port 8080

resource "aws_security_group_rule" "remove_8080" {
  # This rule will be removed
- ingress {
-   from_port   = 8080
-   to_port     = 8080
-   protocol    = "tcp"
-   cidr_blocks = ["0.0.0.0/0"]
-   description = "UNAUTHORIZED - manual change"
- }

  # Allowed rules remain
  ingress {
    from_port   = 443
    to_port     = 443
    protocol    = "tcp"
    cidr_blocks = ["0.0.0.0/0"]
    description = "HTTPS"
  }
  
  ingress {
    from_port   = 80
    to_port     = 80
    protocol    = "tcp"
    cidr_blocks = ["0.0.0.0/0"]
    description = "HTTP (redirect to HTTPS)"
  }
}`,

  runbookWorkflow: `# Runbook Workflow - checkout-service
# Temporal-style workflow definition

workflow:
  name: checkout-service-runbook
  version: "1.0"
  
  deployment:
    steps:
      - id: pre_deploy_checks
        activity: RunHealthChecks
        input:
          service: checkout-service
          checks: [database_connection, redis_connection, stripe_api]
        retries: 3
        timeout: 2m
        
      - id: deploy_canary
        activity: DeployCanary
        input:
          service: checkout-service
          traffic_percentage: 10
        timeout: 5m
        compensation: RollbackCanary
        
      - id: validate_canary
        activity: ValidateMetrics
        input:
          service: checkout-service
          duration: 5m
          thresholds:
            error_rate: 0.1%
            latency_p99: 100ms
        timeout: 10m
        on_failure: rollback
        
      - id: full_rollout
        activity: ProgressiveRollout
        input:
          service: checkout-service
          stages: [25, 50, 75, 100]
          stage_duration: 2m
        timeout: 15m
        compensation: RollbackDeployment
        
  rollback:
    steps:
      - id: revert_deployment
        activity: RevertToLastStable
        input:
          service: checkout-service
        retries: 3
        timeout: 5m
        
      - id: validate_rollback
        activity: ValidateMetrics
        input:
          service: checkout-service
          duration: 2m
        timeout: 5m
        
      - id: notify_team
        activity: SendNotification
        input:
          channels: ["#payments", "@oncall"]
          severity: high
          
  incident_response:
    escalation:
      - level: 1
        schedule: payments-primary
        timeout: 5m
      - level: 2
        schedule: payments-secondary
        timeout: 10m
      - level: 3
        user: payments-lead
        timeout: 15m`
};

// ============================================================================
// OWNERSHIP DATA
// ============================================================================
export const ownershipIssues = [
  {
    id: 'legacy-api',
    service: 'legacy-api',
    issue: 'No owner assigned',
    lastDeployed: '2025-06-15',
    dependencies: 2,
    dependents: 1,
    suggestedOwner: { team: 'Platform', lead: 'Mike Chen' }
  },
  {
    id: 'batch-processor',
    service: 'batch-processor',
    issue: 'No owner assigned',
    lastDeployed: '2025-09-20',
    dependencies: 3,
    dependents: 0,
    suggestedOwner: { team: 'Data', lead: 'Sarah Kim' }
  }
];

export const escalationRoutes = [
  {
    team: 'Payments',
    services: ['checkout-service', 'order-service', 'refund-service'],
    hasRoute: true,
    levels: 4
  },
  {
    team: 'Platform',
    services: ['auth-service', 'api-gateway', 'config-service'],
    hasRoute: true,
    levels: 3
  },
  {
    team: 'Data',
    services: ['payments-db', 'analytics-db', 'cache-cluster'],
    hasRoute: false,
    levels: 0
  }
];
